// react
import { useContext, useEffect, useMemo, useState } from 'react';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import { Avatar, Box, Button, Paper, Stack, Typography } from '@mui/material';

// icons
import {
	ConfirmationNumber as IconVoucher,
	OpenInNew as IconOpenPortal,
} from '@mui/icons-material';

// data
import { COLORS, ConditionContext, VoucherDetails } from '../../../../data';

// components
import { DialogMarkPaymentComplete } from '../../../dialogs/DialogMarkPaymentComplete/DialogMarkPaymentComplete';
import { DialogOverrideCondition } from '../../../dialogs/DialogOverrideCondition/DialogOverrideCondition';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { PaymentsDataTable } from './PaymentsDataTable';
import {
	PAYMENT_BUCKETS,
	PAYMENT_BUCKET_ORDER,
	PaymentBucket,
	resolveBucketFromSlug,
} from './Payments.constants';
import { usePaymentsOverview } from './usePaymentsOverview';

// utils
import {
	getContractCommitmentAmount,
	getContractCurrency,
	getContractNum,
} from '../Contracts/Contracts.utils';
import {
	getProjectLocalCurrency,
	getProjectReportingCurrency,
} from '../../../../utils/localCurrency';
import { resolveContractFxRate } from '../../../../utils/fxRate';

export const Payments = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const {
		activities,
		conditions,
		contracts,
		createException,
		workPlans,
		getExceptionByConditionId,
		getInvoicesByContract,
		loading,
		pagination,
		refetchVouchers,
		setFilterQuery,
		vouchers,
	} = useContext(ContextSpendPlan);

	// params
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'created';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';
	const searchQuery = searchParams.get('search') || '';
	const filterContract = searchParams.get('contract') || '';
	const filterChannel = searchParams.get('channel') || '';

	// Resolve the active bucket from the `?tab=` slug. Unknown/missing values
	// fall back to the default bucket and the URL gets rewritten below so the
	// address bar always shows the canonical slug.
	const rawTab = searchParams.get('tab');
	const { bucket: activeBucket, needsRedirect } = resolveBucketFromSlug(rawTab);

	useEffect(() => {
		if (!needsRedirect) return;
		setSearchParams(
			(params) => {
				params.set('tab', PAYMENT_BUCKETS[activeBucket].slug);
				return params;
			},
			{ replace: true }
		);
	}, [needsRedirect, activeBucket, setSearchParams]);

	// Refetch vouchers when the Payments page mounts (e.g. navigating back from detail)
	useEffect(() => {
		refetchVouchers();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync URL params with context filter query (Payments uses vouchers context).
	// `status` is derived from the active bucket so filtering happens server-side
	// — the table receives only rows for the visible bucket and `pagination.total`
	// is the correct project-wide count for that bucket.
	useEffect(() => {
		setFilterQuery('vouchers', {
			...(searchQuery && { search: searchQuery }),
			...(filterContract && { contract: filterContract }),
			...(filterChannel && { channel: filterChannel }),
			status: PAYMENT_BUCKETS[activeBucket].status,
			page: parseInt(filterPage),
			limit: parseInt(filterLimit),
			orderBy: filterOrderBy,
			order: filterOrder,
		});
	}, [
		filterPage,
		filterLimit,
		filterOrderBy,
		filterOrder,
		searchQuery,
		filterContract,
		filterChannel,
		activeBucket,
		setFilterQuery,
	]);

	// state
	const [overrideCondition, setOverrideCondition] = useState<ConditionContext | null>(null);
	const [completeVoucher, setCompleteVoucher] = useState<VoucherDetails | null>(null);
	const [overviewRefreshToken, setOverviewRefreshToken] = useState(0);

	// Project-wide counts + per-currency amounts per bucket, fetched from the
	// dedicated overview endpoint so the header tiles and tab badges show real
	// totals (not just whatever rows are on the current page).
	const { overview } = usePaymentsOverview(project?.id, overviewRefreshToken);
	const bucketCounts = overview
		? PAYMENT_BUCKET_ORDER.reduce(
				(acc, key) => {
					acc[key] = overview.buckets[key].count;
					return acc;
				},
				{} as Record<PaymentBucket, number>
			)
		: null;

	// handlers for pagination and sorting
	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (orderBy: string, order: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', orderBy);
			params.set('order', order);
			return params;
		});
	};

	const handleTabChange = (bucket: PaymentBucket) => {
		setSearchParams((params) => {
			params.set('tab', PAYMENT_BUCKETS[bucket].slug);
			params.set('page', '1');
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			if (String(value) === 'ALL') {
				params.delete(key);
			} else {
				params.set(key, String(value));
			}
			params.set('page', '1');
			return params;
		});
	};

	// handlers for search
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			params.set('page', '1');
			return params;
		});
	};

	// Helper to extract ID from potentially populated field
	const getId = <T extends { id: number }>(field: number | T | null | undefined): number | null => {
		if (field === null || field === undefined) return null;
		if (typeof field === 'number') return field;
		return field.id;
	};

	// Build enriched vouchers with related data
	const { allVouchers } = useMemo(() => {
		const mapped = vouchers.map((voucher) => {
			const conditionId = getId(voucher.condition);
			const contractId = getId(voucher.contract);
			const activityId = getId(voucher.activity);

			const condition = conditionId ? conditions.find((c) => c.id === conditionId) : null;
			const contract = contractId ? contracts.find((c) => c.id === contractId) : null;
			const activity = activityId
				? activities.find((a) => a.id === activityId)
				: contract
					? activities.find((a) => a.id === contract.planningActivity)
					: null;
			const workPlan = contract
				? workPlans.find((e) => e.id === contract.workPlan)
				: condition
					? workPlans.find((e) => e.id === getId(condition.workPlan))
					: null;
			const currency = voucher.currency || (contract ? getContractCurrency(contract) : 'USD');
			const contractAmount = contract ? getContractCommitmentAmount(contract, workPlan) : null;
			const paymentAmount =
				voucher.amount ||
				condition?.paymentAmount ||
				(condition?.paymentPercentage && contractAmount != null
					? (contractAmount * condition.paymentPercentage) / 100
					: 0);

			let supplierName = 'Unknown Supplier';
			let supplierId = 0;
			if (voucher.recipientEntity) {
				if (typeof voucher.recipientEntity === 'object' && voucher.recipientEntity.name) {
					supplierName = voucher.recipientEntity.name;
					supplierId = voucher.recipientEntity.id;
				} else if (typeof voucher.recipientEntity === 'number') {
					const entity = contract?.entities?.find((e) => e.id === voucher.recipientEntity);
					if (entity) {
						supplierName = entity.name;
						supplierId = entity.id;
					}
				}
			} else if (contract?.entities) {
				const supplier = contract.entities.find((e) => e.type === 'SUPPLIER');
				if (supplier) {
					supplierName = supplier.name;
					supplierId = supplier.id;
				}
			}

			const exception = condition ? getExceptionByConditionId(condition.id) : undefined;

			// Invoice recorded against this milestone (joined via milestoneNumber =
			// condition.id) — feeds the Mark Payment Complete prefill.
			const linkedInvoice =
				condition && contract
					? getInvoicesByContract(contract.id).find(
							(inv) => inv.milestoneNumber === condition.id
						)
					: undefined;

			return {
				id: voucher.id,
				name: condition?.name || `Voucher ${voucher.id}`,
				description: condition?.description || '',
				paymentAmount,
				paymentPercentage: condition?.paymentPercentage || 0,
				status: voucher.status,
				activityId: activity?.id || activityId || 0,
				activityName: activity?.name || contract?.activityName || 'Unknown Activity',
				contractId: contract?.id || contractId || 0,
				contractNum: contract?.num || 'Unknown',
				currency,
				exception: exception
					? {
							id: exception.id,
							activity: activityId || 0,
							activityName: activity?.name || 'Unknown Activity',
							condition: condition?.id || 0,
							conditionName: condition?.name || 'Unknown Condition',
							contract: contractId || 0,
							contractNum: contract?.num || 'Unknown',
							reason: exception.reason,
							overriddenBy:
								typeof exception.overriddenBy === 'number'
									? exception.overriddenBy
									: exception.overriddenBy?.id || 0,
							overriddenByName:
								typeof exception.overriddenBy === 'object' && exception.overriddenBy
									? exception.overriddenBy.fullName || 'Unknown'
									: 'Unknown',
							overriddenAt: exception.created,
							notificationSent: false,
						}
					: undefined,
				supplierId,
				supplierName,
				recipientType: voucher.recipientType || 'VENDOR',
				verifiedAt: condition?.verifiedAt || undefined,
				verifiedBy:
					condition && typeof condition.verifiedBy === 'object' && condition.verifiedBy
						? condition.verifiedBy.fullName || undefined
						: undefined,
				sectionResponses: voucher.sectionResponses || null,
				exchangeRates: voucher.exchangeRates || null,
				mintTransactionHash: voucher.mintTransactionHash,
				redemptionTransactionHash: voucher.redemptionTransactionHash,
				nftTokenId: voucher.nftTokenId,
				invoicePrefill: linkedInvoice
					? {
							num: linkedInvoice.num,
							date: linkedInvoice.date,
							amount: linkedInvoice.amount,
							currency: linkedInvoice.currency,
						}
					: null,
			};
		});

		return { allVouchers: mapped };
	}, [activities, conditions, contracts, workPlans, getExceptionByConditionId, getInvoicesByContract, vouchers]);

	// handlers
	const handleOpenVoucherPage = (voucherId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/voucher/${voucherId}`);
	};

	const handleCloseOverrideDialog = () => {
		setOverrideCondition(null);
	};

	const handleConfirmOverride = async (reason: string) => {
		if (!overrideCondition) return;

		const response = await createException({
			conditionId: overrideCondition.conditionId,
			reason,
		});

		if (response.data) {
			setBanner({
				message: `Exception created for condition "${overrideCondition.conditionName}".`,
				severity: 'success',
			});
		} else if (response.error) {
			setBanner({
				message: `Failed to create exception: ${response.error.message}`,
				severity: 'error',
			});
		}

		handleCloseOverrideDialog();
	};

	// Vouchers come pre-filtered by status from the server (see the
	// `setFilterQuery` call above), so the table receives them directly with no
	// further client-side bucket filtering.
	const filteredVouchers = allVouchers;

	// Contract options for filter dropdown
	const contractOptions = useMemo(
		() =>
			contracts.map((c) => ({
				value: String(c.id),
				label: getContractNum(c) || `Contract ${c.id}`,
			})),
		[contracts]
	);

	// Per-contract local-per-reporting hint, supplied to PaymentsDataTable so
	// each voucher row can defend its restated reporting / local figures
	// against legacy inverted captured rates (pre-PR-3862 vouchers). The hint
	// is the contract's implied budget rate — prefer the commitment scalars,
	// then the contract's own multi-currency amount rows (a USD+ETB contract
	// carries its true rate there even when the scalars are blank). Contracts
	// that yield no rate contribute no entry; the table falls back to captured
	// rates.
	const contractBudgetRates = useMemo(() => {
		const map: Record<number, number> = {};
		for (const c of contracts) {
			const workPlan = workPlans.find((wp) => wp.id === c.workPlan);
			const fx = resolveContractFxRate(c, workPlan, project);
			if (fx && fx.rate > 0) map[c.id] = fx.rate;
		}
		return map;
	}, [contracts, workPlans, project]);

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconVoucher}
				title={t('payments')}
				subtitle={t('spendPlan.payments.subtitle')}
				variant="payment"
				actions={
					<Button
						variant="contained"
						startIcon={<IconOpenPortal />}
						component="a"
						href="https://wbgps.remit.in/"
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							borderRadius: 3,
							fontWeight: 600,
							px: 3,
							py: 1.25,
							textTransform: 'none',
						}}>
						{t('spendPlan.payments.openPortal')}
					</Button>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Payments DataTable with Tabs */}
				<PaymentsDataTable
					data={filteredVouchers}
					isLoading={loading.vouchers}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					orderBy={filterOrderBy}
					order={filterOrder}
					onSortChange={handleSortChange}
					page={parseInt(filterPage)}
					rowsPerPage={parseInt(filterLimit)}
					totalCount={pagination.vouchers?.total || 0}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					onRowClick={(voucher) => handleOpenVoucherPage(voucher.id)}
					onComplete={setCompleteVoucher}
					contractOptions={contractOptions}
					filterValues={{
						...(filterContract && { contract: filterContract }),
						...(filterChannel && { channel: filterChannel }),
					}}
					onFilterChange={handleFilterChange}
					activeBucket={activeBucket}
					onBucketChange={handleTabChange}
					bucketCounts={bucketCounts}
					reportingCurrency={getProjectReportingCurrency(project)}
					localCurrency={getProjectLocalCurrency(project)}
					contractBudgetRates={contractBudgetRates}
				/>

				{/* Help text */}
				<Paper
					elevation={0}
					sx={{
						bgcolor: COLORS.info.light,
						border: '1px solid',
						borderColor: COLORS.info.main,
						borderRadius: 3,
						mt: 3,
						p: 2.5,
					}}>
					<Stack direction="row" alignItems="flex-start" spacing={2}>
						<Avatar
							sx={{
								bgcolor: COLORS.info.main,
								height: 32,
								width: 32,
							}}>
							<IconVoucher sx={{ fontSize: 18 }} />
						</Avatar>
						<Box>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
								{t('spendPlan.payments.voucherFlow')}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<Trans
									i18nKey="spendPlan.payments.voucherFlowDesc"
									defaults="When a supplier provides proof that a condition is met, the system generates a <b>Payment Voucher</b>. Verified vouchers can be redeemed through the <b>Payment Portal</b> via bank transfer, card payment, mobile money, or stablecoin. Each voucher represents a specific payment entitlement linked to a contract milestone."
									components={{ b: <strong /> }}
								/>
							</Typography>
						</Box>
					</Stack>
				</Paper>
			</Box>

			{/* Override Condition Dialog */}
			<DialogOverrideCondition
				condition={overrideCondition}
				isOpen={!!overrideCondition}
				onClose={handleCloseOverrideDialog}
				onConfirm={handleConfirmOverride}
			/>

			{/* Mark Payment Complete Dialog (off-hub) */}
			<DialogMarkPaymentComplete
				voucher={completeVoucher}
				isOpen={!!completeVoucher}
				onClose={() => setCompleteVoucher(null)}
				onSuccess={() => {
					refetchVouchers();
					setOverviewRefreshToken((t) => t + 1);
					setCompleteVoucher(null);
				}}
				projectId={project?.id}
			/>
		</Box>
	);
};
